import type { FC, ReactNode } from 'react'

import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'

interface CtaLinkProps {
    href: string
    children: ReactNode
    variant?: 'primary' | 'outline' | 'pro'
    size?: 'sm' | 'md' | 'lg'
    external?: boolean
    leadingIcon?: ReactNode
    trailingIcon?: ReactNode
    className?: string
    onClick?: () => void
}

const VARIANT_CLASSES = {
    primary: 'bg-primary text-primary-foreground shadow-[0_14px_30px_-14px_rgb(37_99_235/0.8)] hover:brightness-110',
    outline: 'border border-border bg-card text-foreground hover:border-foreground/25 hover:bg-muted',
    pro: 'bg-pro text-pro-foreground shadow-[0_14px_30px_-14px_rgb(109_40_217/0.75)] hover:brightness-110'
}

const SIZE_CLASSES = {
    sm: 'h-10 gap-2 rounded-lg px-4 text-sm',
    md: 'h-12 gap-2.5 rounded-xl px-5 text-[0.95rem]',
    lg: 'h-13 gap-3 rounded-xl px-6 text-base'
}

const CtaLink: FC<CtaLinkProps> = ({
    href,
    children,
    variant = 'primary',
    size = 'md',
    external = false,
    leadingIcon,
    trailingIcon,
    className,
    onClick
}): ReactNode => {
    const trailing = trailingIcon ?? (external ? <ArrowUpRight className='size-4 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5' /> : null)

    return (
        <a
            href={href}
            onClick={onClick}
            target={external ? '_blank' : undefined}
            rel={external ? 'noopener noreferrer' : undefined}
            className={cn(
                'group inline-flex shrink-0 items-center justify-center font-semibold whitespace-nowrap transition-all duration-200 active:scale-[0.98]',
                VARIANT_CLASSES[variant],
                SIZE_CLASSES[size],
                className
            )}
        >
            {leadingIcon}

            <span>
                {children}
            </span>

            {trailing}

            {external && (
                <span className='sr-only'>
                    (opens in a new tab)
                </span>
            )}
        </a>
    )
}

export default CtaLink
